import { LitElement, TemplateResult, html } from 'lit';
import { customElement, queryAssignedElements, property } from 'lit/decorators.js';
import globalStyles from './styles/outline-employee-teaser-card.global.pcss?inline';
import encapsulatedStyles from './styles/outline-employee-teaser-card.encapsulated.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import { screenSizeMap } from '../../../controllers/screen-size-controller';
import { validateSlot } from '../../../utilities/slot';
import '../../outline-icon/outline-icon';

const componentName = 'outline-employee-teaser-card';

/**
 * The OutlineEmployeeTeaserCard component
 * @element outline-employee-teaser-card
 * @slot heading - An outline heading component
 * @slot image - An outline image compoenent
 * @slot department - A span
 * @slot title - A span
 * @slot phone - A basic <a> tag
 * @slot email - A basic <a> tag
 */

@customElement('outline-employee-teaser-card')
export class OutlineEmployeeTeaserCard extends LitElement {
  resizeObserver: ResizeObserver;
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * Ref to elements in the `department` slot
   */
  @queryAssignedElements({slot: 'department'})
  departmentRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `title` slot
   */
  @queryAssignedElements({slot: 'title'})
  titleRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `phone` slot
   */
  @queryAssignedElements({slot: 'phone' })
  phoneRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `email` slot
   */
  @queryAssignedElements({slot: 'email' })
  emailRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `schedule-online` slot
   */
  @queryAssignedElements({slot: 'schedule-online' })
  schOnlineRef: Array<HTMLElement>;
  
  /**
   * Ref to elements in the `schedule-phone` slot
   */
  @queryAssignedElements({slot: 'schedule-phone' })
  schPhoneRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `schedule-inperson` slot
   */
  @queryAssignedElements({slot: 'schedule-inperson' })
  schInPersonRef: Array<HTMLElement>;

  /**
   * If screen is below mobile width
   */
  @property({ type: Boolean, reflect: true })
  isMobile: boolean;

  /**
   * Ref to this
   */
  _selfRef: HTMLElement;
  
  
  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeAtScreen();
    });
  };
  
  
  firstUpdated() {    
    this.setSelfRef()
    validateSlot(this.departmentRef, '.outline-employee-teaser-card--department', this.shadowRoot);
    validateSlot(this.titleRef, '.outline-employee-teaser-card--title', this.shadowRoot);
    validateSlot(this.phoneRef, '.outline-employee-teaser-card--phone', this.shadowRoot);
    validateSlot(this.emailRef, '.outline-employee-teaser-card--email', this.shadowRoot);
    validateSlot(this.schOnlineRef, '.outline-employee-teaser-card--schedule-online', this.shadowRoot);
    validateSlot(this.schPhoneRef, '.outline-employee-teaser-card--schedule-phone', this.shadowRoot);
    validateSlot(this.schInPersonRef, '.outline-employee-teaser-card--schedule-inperson', this.shadowRoot);
    this.resizeObserver.observe(this._selfRef);
  }

  setSelfRef() {
    const self = this.shadowRoot?.querySelector('.outline-employee-teaser-card') as HTMLElement;
    if (self) {      
      this._selfRef = self;
    }
  }

  resizeAtScreen() {
    this.isMobile = this._selfRef.clientWidth < (screenSizeMap.get('xs') as number);
  }

    render(): TemplateResult {
      return html`
        <section class="outline-employee-teaser-card">
          <div class="outline-employee-teaser-card--left-side">
            <div class="outline-employee-teaser-card--heading">
              <slot name="eyebrow"></slot>
              <slot name="heading"></slot>
            </div>
            <div class="outline-employee-teaser-card--position">
              <div class="outline-employee-teaser-card--department">
                <slot name="department"></slot>
              </div>
              <div class="outline-employee-teaser-card--title">
                <slot name="title"></slot>
              </div>
            </div>
            <div class="outline-employee-teaser-card--phone">
              <outline-icon icon='<i class="fa-solid fa-phone"></i>' size='1em'></outline-icon>
              <span class="outline-employee-teaser-card--phone-location">Office:</span>
              <slot name="phone"></slot>
            </div>
            <div class="outline-employee-teaser-card--email">
              <outline-icon icon='<i class="fa-solid fa-envelope"></i>' size='1em'></outline-icon>
              <slot name="email"></slot>
            </div>
            <div class="outline-employee-teaser-card--meeting-info">
              <span class="outline-employee-teaser-card--schedule-label">Schedule a Meeting</span>
              <div class="outline-employee-teaser-card--schedule-links">
                <span class="outline-employee-teaser-card--schedule-online">
                  <slot name="schedule-online"></slot>
                </span>
                <span class="outline-employee-teaser-card--schedule-phone">
                  <slot name="schedule-phone"></slot>
                </span>
                <span class="outline-employee-teaser-card--schedule-inperson">
                  <slot name="schedule-inperson"></slot>
                </span>
              </div>
            </div>
          </div>
          <div class="outline-employee-teaser-card--right-side">
            <div class="outline-employee-teaser-card--image">
              <slot name="image"></slot>
            </div>
          </div>
        </section>
      `;
    } 
}

declare global {
  interface HTMLElementTagNameMap {
      [componentName]: OutlineEmployeeTeaserCard;
  }
}